'use client'

import { useState, useEffect } from 'react'
import { ApplicationWithDetails } from '@/types/landlord'
import { User } from '@/types'
import LandlordChatInterface from './LandlordChatInterface'
import { EmailService } from '@/lib/email'
import { useCurrency } from '@/lib/currency'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/context/AuthContext'

interface ApplicationCardProps {
  application: ApplicationWithDetails
  onStatusUpdate?: () => void
}

type ChatApplication = ApplicationWithDetails & {
  property: { title: string }
  client: User
}

export default function ApplicationCard({ application, onStatusUpdate }: ApplicationCardProps) {
  const [loading, setLoading] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const { user: currentUser } = useAuth()
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'completed' | 'not_found'>('not_found')
  const [checkingPayment, setCheckingPayment] = useState(true)
  const { format } = useCurrency()
  const toast = useToast()

  // Check if landlord can reject (only before payment)
  const canLandlordReject = () => {
    // Can reject pending applications
    if (application.status === 'pending') return true
    // Can reject approved applications only if no payment made
    if ((application.status === 'approved' || application.status === 'accepted') && paymentStatus !== 'completed') {
      return true
    }
    return false
  }

  // Format price using currency context
  const formatPrice = (price: number) => format(price)

  // Get status badge color
  const getStatusColor = (status: string) => {
    const statusColors = {
      pending: 'bg-amber-100 text-amber-800 border border-amber-200',
      accepted: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
      approved: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border border-red-200',
      withdrawn: 'bg-stone-100 text-stone-800 border border-stone-200'
    }
    return statusColors[status as keyof typeof statusColors] || 'bg-stone-100 text-stone-800 border border-stone-200'
  }

  // Check payment status for this application
  const checkPaymentStatus = async () => {
    try {
      setCheckingPayment(true)
      const token = localStorage.getItem('authToken')
      if (!token) {
        setPaymentStatus('not_found')
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/payments/history?applicationId=${application.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        const payments = data.payments || []
        const hasCompletedPayment = payments.some((p: any) => p.status === 'completed' || p.status === 'succeeded')
        setPaymentStatus(hasCompletedPayment ? 'completed' : 'not_found')
      } else {
        setPaymentStatus('not_found')
      }
    } catch (error) {
      console.error('Error checking payment status:', error)
      setPaymentStatus('not_found')
    } finally {
      setCheckingPayment(false)
    }
  }

  // Load current user and check payment status on mount
  useEffect(() => {
    checkPaymentStatus()
  }, [application.id])

  // Handle status update
  const updateStatus = async (newStatus: 'accepted' | 'rejected') => {
    setLoading(true)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        toast.error('Please log in to update application status')
        setLoading(false)
        return
      }

      // Map frontend status to backend status
      // Backend uses 'approved' instead of 'accepted'
      const backendStatus = newStatus === 'accepted' ? 'approved' : 'rejected'

      const response = await fetch(`${apiUrl}/applications/${application.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: backendStatus
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to ${newStatus} application`)
      }

      const data = await response.json()
      console.log('Application status updated:', data)

      // Send email notification
      try {
        await EmailService.sendApplicationStatusEmail(application, newStatus)
      } catch (emailError) {
        console.error('Email notification error:', emailError)
        // Don't block the flow if email fails
      }

      toast.success(`Application ${newStatus} successfully!`)
      onStatusUpdate?.()
    } catch (error) {
      console.error('Status update error:', error)
      toast.error(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle chat button click
  const handleChatClick = () => {
    setShowChat(true)
  }

  // Check if chat should be available
  // Chat is available once application is accepted/approved (no need to wait for payment)
  const isChatAvailable = () => {
    return application.status === 'accepted' || application.status === 'approved'
  }

  // Get chat button info
  const getChatButtonInfo = () => {
    if (checkingPayment) {
      return {
        text: 'Checking Payment...',
        disabled: true,
        className: 'bg-gray-300 text-gray-500 cursor-not-allowed'
      }
    }

    if (application.status === 'rejected' || application.status === 'withdrawn') {
      return {
        text: 'Chat Unavailable',
        disabled: true,
        className: 'bg-gray-300 text-gray-500 cursor-not-allowed'
      }
    }

    if (application.status === 'pending') {
      return {
        text: 'Chat After Acceptance',
        disabled: true,
        className: 'bg-gray-300 text-gray-500 cursor-not-allowed'
      }
    }

    // Chat is available once application is accepted/approved (no need to wait for payment)
    if (application.status === 'accepted' || application.status === 'approved') {
      return {
        text: 'Chat with Client',
        disabled: false,
        className: 'bg-emerald-600 text-white hover:bg-emerald-700'
      }
    }

    return {
      text: 'Chat Unavailable',
      disabled: true,
      className: 'bg-gray-300 text-gray-500 cursor-not-allowed'
    }
  }

  const chatButtonInfo = getChatButtonInfo()

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:shadow-lg transition-all duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 space-y-3 sm:space-y-0">
        <div className="flex-1">
          <h3 className="text-xl font-display text-stone-900 break-words mb-1">
            {application.property.title}
          </h3>
          <p className="text-stone-600 text-sm sm:text-base">{application.property.location}</p>
        </div>
        <div className="text-left sm:text-right">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(application.status)}`}>
            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
          </span>
          
          {/* Payment Status Indicator */}
          {(application.status === 'accepted' || application.status === 'approved') && (
            <div className="text-xs mt-2 flex items-center gap-2">
              {checkingPayment ? (
                <span className="text-stone-500">Checking payment...</span>
              ) : paymentStatus === 'completed' ? (
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Payment Completed
                </span>
              ) : paymentStatus === 'pending' ? (
                <span className="text-amber-600 font-semibold">⏳ Payment Pending</span>
              ) : (
                <span className="text-orange-600 font-semibold">💳 Payment Required</span>
              )}
              
              {/* Refresh button for payment status */}
              <button
                onClick={checkPaymentStatus}
                disabled={checkingPayment}
                className="text-emerald-600 hover:text-emerald-700 disabled:text-stone-400 disabled:cursor-not-allowed transition-colors"
                title="Refresh payment status"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Applicant Info */}
      <div className="mb-6">
        <h4 className="font-semibold text-stone-900 mb-3 text-base">Applicant Details</h4>
        <div className="bg-stone-50 rounded-xl p-4 space-y-2 border border-stone-200">
          {application.client?.kyc_data?.firstName || application.client?.kyc_data?.lastName ? (
            <p className="text-sm text-stone-700">
              <span className="font-semibold">Name:</span> {[
                application.client.kyc_data.firstName,
                application.client.kyc_data.lastName
              ].filter(Boolean).join(' ')}
            </p>
          ) : null}
          <p className="text-sm text-stone-700">
            <span className="font-semibold">Applied:</span> {(() => {
              try {
                const date = new Date(application.created_at || (application as any).applicationDate || (application as any).createdAt)
                return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString()
              } catch {
                return 'Invalid Date'
              }
            })()}
          </p>
          <p className="text-sm text-stone-700">
            <span className="font-semibold">Rent:</span> {formatPrice(application.property.price)}
            {(application.property as any).rentalType === 'short-term' ? '/day' : '/month'}
          </p>
          {/* Rental Type - Prominently displayed */}
          {(application.property as any).rentalType && (
            <p className="text-sm text-stone-700">
              <span className="font-semibold">Rental Type:</span> 
              <span className={`ml-2 px-3 py-1 rounded-full text-xs font-bold ${
                (application.property as any).rentalType === 'short-term' 
                  ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
              }`}>
                {(application.property as any).rentalType === 'short-term' ? 'Short-term (Shortlet)' : 'Long-term Rental'}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Application Details - Always show if there's any application data */}
      <div className="mb-6">
        <h4 className="font-semibold text-stone-900 mb-3 text-base">Application Details</h4>
        <div className="bg-stone-50 rounded-xl p-4 space-y-3 border border-stone-200">
          {/* Rental Type - Prominently displayed at the top */}
          {(application.property as any).rentalType && (
            <div className="mb-3 pb-3 border-b border-stone-200">
              <p className="text-sm text-stone-700 mb-2">
                <span className="font-semibold">Rental Type:</span>
              </p>
              <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold ${
                (application.property as any).rentalType === 'short-term' 
                  ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
              }`}>
                {(application.property as any).rentalType === 'short-term' ? 'Short-term (Shortlet)' : 'Long-term Rental'}
              </span>
            </div>
          )}

          {/* Duration - For Long-term Rentals - Always show if available */}
          {((application as any).lease_duration || (application as any).preferences?.leaseLength || (application as any).preferences?.leaseDuration) && (
            <div className="mb-2">
              <p className="text-sm text-gray-600 font-semibold">
                <span className="font-medium">Rental Duration:</span> {
                  (application as any).lease_duration || (application as any).preferences?.leaseLength || (application as any).preferences?.leaseDuration
                } {(() => {
                  const duration = (application as any).lease_duration || (application as any).preferences?.leaseLength || (application as any).preferences?.leaseDuration
                  return duration === 1 ? 'month' : 'months'
                })()}
              </p>
              <p className="text-xs text-stone-500 mt-1">The client wants to rent for this duration</p>
            </div>
          )}
          
          {/* Booking Dates and Duration - For Short-term Rentals - Always show if available */}
          {((application as any).booking_start_date || (application as any).preferences?.bookingStartDate || (application as any).bookingDates?.start) && (
            <div className="mb-2">
              <p className="text-sm text-stone-700 font-semibold mb-2">
                <span className="font-semibold">Booking Period:</span>
              </p>
              <div className="ml-2 space-y-1">
                <p className="text-sm text-stone-700">
                  <span className="font-semibold">Check-in:</span> {(() => {
                    try {
                      const date = new Date((application as any).booking_start_date || (application as any).preferences?.bookingStartDate || (application as any).bookingDates?.start)
                      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString()
                    } catch {
                      return 'Invalid Date'
                    }
                  })()}
                </p>
                <p className="text-sm text-stone-700">
                  <span className="font-semibold">Check-out:</span> {(() => {
                    try {
                      const date = new Date((application as any).booking_end_date || (application as any).preferences?.bookingEndDate || (application as any).bookingDates?.end)
                      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString()
                    } catch {
                      return 'Invalid Date'
                    }
                  })()}
                </p>
                {/* Calculate and display number of days */}
                {((application as any).booking_start_date || (application as any).preferences?.bookingStartDate || (application as any).bookingDates?.start) &&
                  ((application as any).booking_end_date || (application as any).preferences?.bookingEndDate || (application as any).bookingDates?.end) && (
                  <p className="text-sm text-stone-700 font-semibold">
                    <span className="font-semibold">Rental Duration:</span> {(() => {
                      try {
                        const start = new Date((application as any).booking_start_date || (application as any).preferences?.bookingStartDate || (application as any).bookingDates?.start)
                        const end = new Date((application as any).booking_end_date || (application as any).preferences?.bookingEndDate || (application as any).bookingDates?.end)
                        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                          const diffTime = Math.abs(end.getTime() - start.getTime())
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                          return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`
                        }
                        return 'N/A'
                      } catch {
                        return 'N/A'
                      }
                    })()}
                  </p>
                )}
              </div>
              <p className="text-xs text-stone-500 mt-1">The client wants to rent for this duration</p>
            </div>
          )}
          
          {/* Total Amount - For Short-term Rentals - Always show if available */}
          {((application as any).total_amount || (application as any).preferences?.totalAmount || (application as any).bookingDates?.totalAmount) && (
            <div className="mb-2">
              <p className="text-sm text-stone-700 font-semibold">
                <span className="font-semibold">Total Amount:</span> {formatPrice(
                  (application as any).total_amount || (application as any).preferences?.totalAmount || (application as any).bookingDates?.totalAmount || 0
                )}
              </p>
              <p className="text-xs text-stone-500 mt-1">Total cost for the rental period</p>
            </div>
          )}

          {/* Move-in Date - For Long-term Rentals */}
          {((application as any).move_in_date || (application as any).preferences?.moveInDate) && (
            <p className="text-sm text-stone-700">
              <span className="font-semibold">Preferred Move-in Date:</span> {(() => {
                try {
                  const date = new Date((application as any).move_in_date || (application as any).preferences?.moveInDate)
                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString()
                } catch {
                  return 'Invalid Date'
                }
              })()}
            </p>
          )}

          {/* Client Comment/Message */}
          {(application.message || (application as any).preferences?.message || (application as any).preferences?.additionalInfo) && (
            <div className="mt-3 pt-3 border-t border-stone-200">
              <p className="text-sm text-stone-700 font-semibold mb-2">Client Comment:</p>
              <p className="text-sm text-stone-800 whitespace-pre-line break-words bg-white p-3 rounded-lg border border-stone-200">
                {application.message || (application as any).preferences?.message || (application as any).preferences?.additionalInfo}
              </p>
            </div>
          )}
          
          {application.monthly_income && (
            <p className="text-sm text-stone-700">
              <span className="font-semibold">Monthly Income:</span> {formatPrice(application.monthly_income)}/month
            </p>
          )}
          {application.employment_status && (
            <p className="text-sm text-stone-700">
              <span className="font-semibold">Employment Status:</span> {application.employment_status}
            </p>
          )}
        </div>
      </div>

      {/* KYC Documents */}
      {application.kyc_docs && Object.keys(application.kyc_docs).length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-stone-900 mb-3 text-base">KYC Documents</h4>
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
            <div className="space-y-2">
              {Object.entries(application.kyc_docs).map(([docType, url]) => (
                <div key={docType} className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0 p-2 hover:bg-white rounded-lg transition-colors">
                  <span className="text-sm text-stone-700 capitalize font-medium">
                    {docType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                  </span>
                  <a
                    href={url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-semibold flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Document
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-stone-100">
        {/* Chat button */}
        <button
          onClick={handleChatClick}
          disabled={chatButtonInfo.disabled}
          className={`flex-1 px-6 py-3 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ${
            chatButtonInfo.disabled 
              ? 'bg-stone-200 text-stone-500' 
              : 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-500/25'
          }`}
        >
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3.13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm sm:text-base">{chatButtonInfo.text}</span>
          </div>
        </button>

        {/* Status update buttons - only for pending applications */}
        {application.status === 'pending' && (
          <>
            <button
              onClick={() => updateStatus('accepted')}
              disabled={loading}
              className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25"
            >
              {loading ? 'Processing...' : 'Accept'}
            </button>
            <button
              onClick={() => updateStatus('rejected')}
              disabled={loading}
              className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25"
            >
              {loading ? 'Processing...' : 'Reject'}
            </button>
          </>
        )}

        {/* Payment status and reject button for approved applications */}
        {(application.status === 'approved' || application.status === 'accepted') && (
          <>
            {paymentStatus === 'completed' ? (
              <div className="w-full p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-800 font-semibold text-sm flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Payment Received
                    </p>
                    <p className="text-emerald-700 text-xs mt-1">Payment is held in escrow</p>
                  </div>
                  <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full border border-emerald-200">
                    In Escrow
                  </span>
                </div>
              </div>
            ) : canLandlordReject() ? (
              <button
                onClick={() => updateStatus('rejected')}
                disabled={loading || checkingPayment}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25"
                title={checkingPayment ? 'Checking payment status...' : 'Reject application (only before payment)'}
              >
                {loading ? 'Processing...' : 'Reject'}
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Chat Availability Info */}
      {(application.status === 'accepted' || application.status === 'approved') && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-emerald-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-emerald-800">
              <p className="font-semibold mb-2">Chat Availability:</p>
              <ul className="text-xs space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span><strong>Pending:</strong> Chat available after accepting application</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span><strong>Accepted:</strong> Chat enabled - communicate with your client!</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span><strong>Rejected:</strong> Chat disabled</span>
                </li>
              </ul>
              <p className="text-emerald-700 font-semibold mt-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                You can now chat with the client!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {application.status !== 'pending' && (
        <div className={`p-4 rounded-xl text-sm border mt-4 ${
          (application.status === 'accepted' || application.status === 'approved')
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {(application.status === 'accepted' || application.status === 'approved') ? (
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span className="font-semibold">
              Application {application.status === 'approved' ? 'approved' : application.status} on {(() => {
            try {
              // Use reviewedAt if available (when application was reviewed/approved/rejected)
              // Otherwise fall back to updated_at or created_at
              const dateStr = (application as any).reviewedAt || (application as any).reviewed_at || application.updated_at || (application as any).updatedAt || application.created_at || (application as any).createdAt || (application as any).applicationDate
              const date = new Date(dateStr)
              if (isNaN(date.getTime())) {
                return 'Date not available'
              }
              return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })
            } catch {
              return 'Date not available'
            }
          })()}
            </span>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && currentUser && isChatAvailable() && (
        <LandlordChatInterface
          application={application as ChatApplication}
          currentUser={currentUser as any as User}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  )
}

